<script lang="ts" setup>
import { computed, provide, ref, watch } from "vue";

import ExpandButton from "@/components/buttons/ExpandButton.vue";

const TABLE_FILTER_SYMBOL = Symbol.for("TableFilter");

provide("tableFilterSymbol", TABLE_FILTER_SYMBOL);

const props = withDefaults(
  defineProps<{
    expandable?: boolean;
    noActions?: boolean;
    filters?: Record<string, unknown> | null;
    headerFilterKeys?: string[] | null;
  }>(),
  {
    expandable: true,
    noActions: false,
    filters: null,
    headerFilterKeys: () => [],
  },
);

const emit = defineEmits<{
  (e: "clear"): void;
  (e: "apply"): void;
  (e: "expand"): void;
}>();

const expanded = ref(false);

const hasActiveBodyFilters = computed(() => {
  const { headerFilterKeys, filters, expandable } = props;

  if (!headerFilterKeys || !expandable || !filters) {
    return false;
  }

  return Object.entries(filters).some(([key, value]) => {
    return !headerFilterKeys.includes(key) && value;
  });
});

watch(
  hasActiveBodyFilters,
  (value) => {
    if (value) {
      expanded.value = true;
    }
  },
  { immediate: true },
);

function clearFilters() {
  emit("clear");
}

function applyFilters() {
  emit("apply");
}

function toggleExpand() {
  expanded.value = !expanded.value;
  emit("expand");
}
</script>

<template>
  <v-card class="mb-4 rounded-0 table-filter" color="white" variant="flat">
    <v-card-text class="pa-0">
      <form autocomplete="off" class="pt-4 pb-4 pr-6 pl-6" role="presentation">
        <v-row>
          <v-col>
            <slot name="header"></slot>
          </v-col>
          <v-col v-if="!noActions" :style="{ flex: '0 0 160px' }" class="d-flex">
            <v-btn
              id="search--reset-btn"
              class="mr-2 font-weight-600 text-uppercase"
              color="primary"
              height="40"
              variant="outlined"
              @click="clearFilters"
            >
              Clear
            </v-btn>
            <v-btn
              id="search--apply-btn"
              class="mr-2 text-uppercase font-weight-600 text-uppercase"
              color="primary"
              height="40"
              variant="flat"
              @click="applyFilters"
            >
              Apply
            </v-btn>
            <expand-button v-if="expandable" id="search--additional-btn" :expanded="expanded" @click="toggleExpand" />
          </v-col>
        </v-row>
        <div v-if="expanded">
          <v-divider class="mt-4 mb-4" />
          <slot></slot>
        </div>
      </form>
    </v-card-text>
  </v-card>
</template>

<style lang="scss" scoped>
.table-filter {
  &--btn {
    height: 44px !important;

    &--clear {
      width: 44px !important;
      min-width: 44px !important;
    }
  }
}
</style>
